import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {message} from 'antd';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';
import {handleApiCallInStore} from './utils';
import {changeEnabledStatus, http, updateItemById} from '@/utils';
import {
  EnabledStatus,
  ICreateOrUpdateGig,
  ICreateOrUpdateItem,
  IGigApplicationItem,
  IGigData,
  IGigNotificationsHistoryItem,
  IGigNotificationsPayload,
  IGigsItem,
  IGigsLiteItem,
  IPriceTypes,
  ISegmentedCoachesItem,
  ISegmentedGigCoachesPayload,
  ListResponse,
} from '@/models';
import {API_ENDPOINTS, LoadingKeys} from '@/constants';

export interface IGigsStore {
  nbHits: number;
  segmentedCoachesNbHits: number;
  notificationsHistoryNbHits: number;
  isHistoryModalOpen: boolean;
  segmentedCoachesList: ISegmentedCoachesItem[];
  applicationsNbHits: number;
  gigsLiteList: IGigsLiteItem[];
  gigDetails: IGigData;
  gigsList: IGigsItem[];
  priceTypes: IPriceTypes[];
  notificationsHistoryList: IGigNotificationsHistoryItem[];
  gigsApplications: IGigApplicationItem[];
  getGigsList: (params) => void;
  changeGigStatus: (payload: EnabledStatus) => void;
  getPriceTypes: () => void;
  getGigById: (gigId: string, onSuccess: (data: IGigData) => void) => void;
  createOrUpdateGig: (data: ICreateOrUpdateItem<ICreateOrUpdateGig>) => void;
  getGigsApplications: (params, id: string) => void;
  getAllGigsLite: () => void;
  changeApplicationStatus: (payload: {
    id: string;
    status: 'accepted' | 'rejected';
  }) => void;
  getSegmentedCoaches: (
    payload: ISegmentedGigCoachesPayload,
    onSuccess?: (countOfCoaches: number) => void
  ) => void;
  sendGigNotification: (
    payload: IGigNotificationsPayload,
    onSuccess: () => void
  ) => void;
  getNotificationsHistory: (params) => void;
  searchOnCoachByNotificationId: (notificationId: string, params) => void;
  changeHistoryModalStatus: (isOpen: boolean) => void;
}

export const useGigsStore = create(
  persist<IGigsStore>(
    set => ({
      isHistoryModalOpen: false,
      notificationsHistoryNbHits: 0,
      segmentedCoachesNbHits: 0,
      segmentedCoachesList: [],
      notificationsHistoryList: [],
      gigsLiteList: [],
      applicationsNbHits: 0,
      priceTypes: [],
      gigsList: [],
      gigsApplications: [],
      gigDetails: undefined,
      nbHits: 0,
      changeHistoryModalStatus: async isOpen => {
        set(() => ({isHistoryModalOpen: isOpen}));
      },
      changeApplicationStatus: async ({id, status}) => {
        handleApiCallInStore(LoadingKeys.ChangeApplicationStatus, async () => {
          await http.put<{ status: 'accepted' | 'rejected' }>(
            API_ENDPOINTS.gigs.changeApplicationStatus(id),
            {status}
          );
          set(({gigsApplications}) => ({
            gigsApplications: gigsApplications.map(item => {
              if (item.objectID === id) {
                return {...item, status};
              }
              return item;
            }),
          }));
          if (status === 'accepted') {
            message.success(t('gigApplicationAcceptedSuccessfully'));
          } else {
            message.success(t('gigApplicationRejectedSuccessfully'));
          }
        });
      },
      sendGigNotification: async (payload, onSuccess) => {
        handleApiCallInStore(LoadingKeys.sendGigNotification, async () => {
          await http.post(
            API_ENDPOINTS.gigs.sendGigNotification(payload?.id),
            payload
          );
          await onSuccess();
        });
      },
      getGigById: async (gigId, onSuccess) => {
        handleApiCallInStore(LoadingKeys.getGigById, async () => {
          const {data} = await http.get<IGigData>(
            API_ENDPOINTS.gigs.byId(gigId)
          );
          set(() => ({gigDetails: data}));
          await onSuccess(data);
        });
      },
      getSegmentedCoaches: async (payload, onSuccess) => {
        handleApiCallInStore(LoadingKeys.getGigSegmentedCoaches, async () => {
          const {data} = await http.post<
            ISegmentedGigCoachesPayload,
            {
              data: {
                coachList: ISegmentedCoachesItem[];
                countOfCoaches: number;
                totalPages: number;
              };
            }
          >(API_ENDPOINTS.gigs.segmentedCoaches, payload);
          set(() => ({
            segmentedCoachesNbHits: data?.countOfCoaches,
            segmentedCoachesList: data?.coachList,
          }));
          if (onSuccess) {
            await onSuccess(data?.countOfCoaches);
          }
        });
      },
      getAllGigsLite: async () => {
        handleApiCallInStore(LoadingKeys.getGigById, async () => {
          const {data} = await http.get<{ items: IGigsLiteItem[] }>(
            API_ENDPOINTS.gigs.liteList
          );
          set(() => ({gigsLiteList: data?.items}));
        });
      },

      createOrUpdateGig: ({id, payload, onSuccess}) => {
        handleApiCallInStore(LoadingKeys.createOrUpdateGig, async () => {
          const apiLink = id
            ? API_ENDPOINTS.gigs.updateGig(id)
            : API_ENDPOINTS.gigs.createGig;
          if (id) {
            await http.put(apiLink, payload);
          } else {
            await http.post(apiLink, payload);
          }
          await onSuccess();
        });
      },
      getPriceTypes: () => {
        handleApiCallInStore(LoadingKeys.PriceTypes, async () => {
          const {data} = await http.get<{ items: IPriceTypes[] }>(
            API_ENDPOINTS.gigs.priceTypes
          );
          set(() => ({
            priceTypes: data?.items ?? [],
          }));
        });
      },
      changeGigStatus: (payload: EnabledStatus) => {
        handleApiCallInStore(LoadingKeys.changeGigStatus, async () => {
          const {id, isEnabled} = payload;
          await http.put(API_ENDPOINTS.gigs.changeStatus(id), {
            id,
            isEnabled,
          });

          set(({gigsList}) => ({
            gigsList: updateItemById(gigsList, 'isEnabled', isEnabled, id),
          }));
          changeEnabledStatus(isEnabled, t('gig'));
        });
      },
      getGigsList: async params => {
        handleApiCallInStore(LoadingKeys.GetGigsList, async () => {
          const {data} = await http.get<ListResponse<IGigsItem>>(
            API_ENDPOINTS.gigs.list,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({gigsList: items ?? [], nbHits}));
        });
      },
      getNotificationsHistory: async params => {
        handleApiCallInStore(
          LoadingKeys.getNotificationsHistoryList,
          async () => {
            const {data} = await http.get<
              ListResponse<IGigNotificationsHistoryItem>
            >(API_ENDPOINTS.gigs.notificationsHistoryList, {
              params,
            } as AxiosRequestConfig);
            const {items, nbHits} = data;
            set(() => ({
              notificationsHistoryList: items ?? [],
              notificationsHistoryNbHits: nbHits,
            }));
          }
        );
      },
      searchOnCoachByNotificationId: async (notificationId: string, params) => {
        handleApiCallInStore(
          LoadingKeys.searchOnCoachByNotificationId,
          async () => {
            const {data} = await http.get<{ items: ISegmentedCoachesItem[] }>(
              API_ENDPOINTS.gigs.searchOnSegmentedCoachByNotificationId(
                notificationId
              ),
              {
                params: {...params, pageNumber: 0, limit: 25},
              } as AxiosRequestConfig
            );
            const {items} = data;
            set(() => ({
              segmentedCoachesList: items ?? [],
              notificationsHistoryNbHits: (items ?? []).length,
            }));
          }
        );
      },
      getGigsApplications: async (params, id) => {
        handleApiCallInStore(LoadingKeys.GetGigsApplications, async () => {
          const endPoint = id
            ? API_ENDPOINTS.gigs.gigRelatedApplicationsList(id)
            : API_ENDPOINTS.gigs.applicationsList;
          const {data} = await http.get<ListResponse<IGigApplicationItem>>(
            endPoint,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({
            gigsApplications: items ?? [],
            applicationsNbHits: nbHits,
          }));
        });
      },
    }),
    {
      name: 'Gigs-storage',

    }
  )
);
